// The Description page's target lists: each target a word's characters, pinyin and meaning, or a grammar point's
// pattern, explanation and example, which "Add pages from a PDF" adds and the teacher edits one at a time
// (TargetEntryDialog). The lists are the lesson's saved targets: their changes are saved with the lesson, in one write
// (IVerbaScholaTargetChanges, controller/verbaschola_lesson_controller.py target_changes).
import type { IVerbaScholaLessonGrammarTarget, IVerbaScholaLessonVocabularyTarget, IVerbaScholaTargetChanges } from '../api/VerbaScholaApi';
import { samePoint } from './pdfImport/pdfTargets';
import { editablePinyin } from './studentInstructionalPinyin';

export interface VocabularyEntry { key: string; id?: number; hanzi: string; pinyin: string; meaning: string; source?: string }
export interface GrammarEntry { key: string; id?: number; pattern: string; meaning: string; example: string; source?: string }

/** The two lists as edited, with the saved targets removed from them (saved when the lesson is). */
export interface TargetLists { vocabulary: VocabularyEntry[]; grammar: GrammarEntry[]; removedVocabulary: number[]; removedGrammar: number[] }

let lastKey = 0;
/** A key for an entry not saved yet. */
export const newEntryKey = () => `new-${++lastKey}`;

export function targetLists(vocabulary: IVerbaScholaLessonVocabularyTarget[], grammar: IVerbaScholaLessonGrammarTarget[]): TargetLists {
    return {
        vocabulary: vocabulary.map(target => ({ key: `saved-${target.id}`, id: target.id, hanzi: target.hanzi, pinyin: editablePinyin(target.pinyin),
            meaning: target.meaning ?? '', source: target.source })),
        grammar: grammar.map(target => ({ key: `saved-${target.id}`, id: target.id, pattern: target.pattern, meaning: target.meaning ?? '',
            example: target.example ?? '', source: target.source })),
        removedVocabulary: [],
        removedGrammar: [],
    };
}

const optional = (value: string) => value.trim() || null;
/** Pinyin as typed, one syllable per space or comma; the server splits syllables written together. */
export const pinyinWords = (value: string) => (value.trim() ? value.trim().split(/[,\s]+/).filter(Boolean) : null);

/** An entry that holds something: a new one left empty is not a target. */
const filled = (entry: VocabularyEntry | GrammarEntry) => Object.entries(entry)
    .some(([key, value]) => !['key', 'id', 'source'].includes(key) && typeof value === 'string' && value.trim());

/** Entries that cannot be saved as they are, by key, with what to do: one with something written but no word (or
 * pattern), and a new one the list already holds (the lesson holds each word or point once). */
export function entryProblems(lists: TargetLists): Map<string, string> {
    const problems = new Map<string, string>();
    lists.vocabulary.forEach((entry, index) => {
        const word = entry.hanzi.trim();
        if (!word && (entry.id !== undefined || filled(entry))) problems.set(entry.key, "Write the word's characters, or remove it.");
        else if (word && entry.id === undefined && lists.vocabulary.slice(0, index).some(other => other.hanzi.trim() === word)) {
            problems.set(entry.key, `${word} is already in the list: remove one.`);
        }
    });
    lists.grammar.forEach((entry, index) => {
        const pattern = entry.pattern.trim();
        if (!pattern && (entry.id !== undefined || filled(entry))) problems.set(entry.key, 'Write the pattern, or remove it.');
        else if (pattern && entry.id === undefined && lists.grammar.slice(0, index).some(other => samePoint(other.pattern, pattern))) {
            problems.set(entry.key, `${pattern} is already in the list: remove one.`);
        }
    });
    return problems;
}

/** The keys of the entries not saved as they are: added, or changed since saved. */
export function unsavedEntries(lists: TargetLists, savedVocabulary: IVerbaScholaLessonVocabularyTarget[],
    savedGrammar: IVerbaScholaLessonGrammarTarget[]): Set<string> {
    const saved = targetLists(savedVocabulary, savedGrammar);
    const vocabulary = new Map(saved.vocabulary.map(entry => [entry.id, entry]));
    const grammar = new Map(saved.grammar.map(entry => [entry.id, entry]));
    return new Set([
        ...lists.vocabulary.filter(entry => {
            const before = entry.id === undefined ? undefined : vocabulary.get(entry.id);
            return !before || before.hanzi !== entry.hanzi || before.pinyin !== entry.pinyin || before.meaning !== entry.meaning;
        }).map(entry => entry.key),
        ...lists.grammar.filter(entry => {
            const before = entry.id === undefined ? undefined : grammar.get(entry.id);
            return !before || before.pattern !== entry.pattern || before.meaning !== entry.meaning || before.example !== entry.example;
        }).map(entry => entry.key),
    ]);
}

/** The lists' changes to the saved targets, by scope; undefined when there are none. */
export function targetChanges(lists: TargetLists, savedVocabulary: IVerbaScholaLessonVocabularyTarget[],
    savedGrammar: IVerbaScholaLessonGrammarTarget[]): IVerbaScholaTargetChanges | undefined {
    const saved = targetLists(savedVocabulary, savedGrammar);
    const vocabularyBefore = new Map(saved.vocabulary.map(entry => [entry.id, entry]));
    const grammarBefore = new Map(saved.grammar.map(entry => [entry.id, entry]));
    const vocabulary = {
        added: lists.vocabulary.filter(entry => entry.id === undefined && filled(entry)).map(entry => ({
            hanzi: entry.hanzi.trim(), pinyin: pinyinWords(entry.pinyin), meaning: optional(entry.meaning), source: entry.source ?? 'added', is_review: false })),
        changed: lists.vocabulary.filter(entry => {
            const before = entry.id === undefined ? undefined : vocabularyBefore.get(entry.id);
            return before && (before.hanzi !== entry.hanzi || before.pinyin !== entry.pinyin || before.meaning !== entry.meaning);
        }).map(entry => ({ id: entry.id, hanzi: entry.hanzi.trim(), pinyin: pinyinWords(entry.pinyin), meaning: optional(entry.meaning) })),
        removed: lists.removedVocabulary,
    };
    const grammar = {
        added: lists.grammar.filter(entry => entry.id === undefined && filled(entry)).map(entry => ({
            pattern: entry.pattern.trim(), meaning: optional(entry.meaning), example: optional(entry.example), source: entry.source ?? 'added' })),
        changed: lists.grammar.filter(entry => {
            const before = entry.id === undefined ? undefined : grammarBefore.get(entry.id);
            return before && (before.pattern !== entry.pattern || before.meaning !== entry.meaning || before.example !== entry.example);
        }).map(entry => ({ id: entry.id, pattern: entry.pattern.trim(), meaning: optional(entry.meaning), example: optional(entry.example) })),
        removed: lists.removedGrammar,
    };
    const any = (changes: { added: unknown[]; changed: unknown[]; removed: unknown[] }) => Boolean(changes.added.length || changes.changed.length || changes.removed.length);
    if (!any(vocabulary) && !any(grammar)) return undefined;
    return { ...(any(vocabulary) ? { vocabulary } : {}), ...(any(grammar) ? { grammar } : {}) };
}
